/* eslint-disable no-console */
import React, { useMemo, useCallback, useState } from 'react';
import styled from 'styled-components';
import { useField, useForm } from 'react-final-form';
import { isSensitiveFieldKey } from '@ubeya/shared/utils/sensitiveFieldLists';
import {
  Button as BaseButton,
  CheckBox as BaseCheckBox,
  ExplanationTooltip,
  Modal,
  SelectField as BaseSelectField
} from '@ubeya/shared-web/components';
import { SORT_BY_BOOLEAN_NO_KEY } from '@ubeya/shared/utils/sorting';
import { FieldTypes } from '@ubeya/shared/hooks/account/useFields';
import { required } from '@ubeya/shared/utils/form';
import FieldModal from '../../Fields/FieldModal';

const FieldsArea = styled.div`
  padding: 10px 40px;
`;

const PositionComplianceModalWrapper = styled.div`
  .modal-wrapper {
    z-index: 5;
  }
`;

const Container = styled.div`
  position: relative;
`;

const CheckBox = styled(BaseCheckBox)`
  margin-top: 10px;
`;

const Total = styled.div`
  margin: 20px;
  color: ${({ theme }) => theme.colors.black};
`;

const Selected = styled.div`
  color: ${({ theme }) => theme.colors.primary};
  display: inline;
  margin-right: 2px;
`;

const SelectField = styled(BaseSelectField)`
  margin: 10px 0 20px 45px;
`;

const Button = styled(BaseButton)`
  position: absolute;
  top: 20px;
  right: 20px;
`;

const PositionComplianceModal = ({ setComplianceChanged, mappedFields, addField, t, onClose }) => {
  const form = useForm();
  // setState without setter function is for keep the initial value of form values
  // from the moment the component is mounted
  const [initialValues] = useState(form.getState().values);

  const {
    input: { value: complianceFieldsIds, onChange: onChangeComplianceFieldsIds }
  } = useField('compliance.fieldsIds');
  const {
    input: { value: complianceFieldsValues }
  } = useField('compliance.fieldsValues');

  const {
    input: { onChange: onComplianceChange, value: complianceValue }
  } = useField('compliance');

  const [selectedField, setSelectedField] = useState();

  const keySorterByChecked = useCallback(
    (a, b) => SORT_BY_BOOLEAN_NO_KEY(complianceFieldsIds[a], complianceFieldsIds[b]),
    [complianceFieldsIds]
  );

  /* Sort only in first render not in every change */
  const [sortedSelectedFieldsKeys, setSortedSelectedFieldsKeys] = useState(
    Object.keys(complianceFieldsIds).sort(keySorterByChecked)
  );

  const totalSelected = useMemo(
    () =>
      Object.entries(complianceFieldsIds).filter(([key, value]) => !!value && !isSensitiveFieldKey(key, mappedFields))
        .length,
    [complianceFieldsIds, mappedFields]
  );

  const onSelectionChange = useCallback(
    (fieldIdFormatted, value) => {
      onChangeComplianceFieldsIds({
        ...complianceFieldsIds,
        [fieldIdFormatted]: value
      });
    },
    [complianceFieldsIds, onChangeComplianceFieldsIds]
  );

  const handleAddField = useCallback(
    async (field) => {
      // compliance field needs to be auto shared with employee
      const newFieldData = await addField(field);

      const fieldIdFormatted = `field-${newFieldData?.data?.id}`;

      onSelectionChange(fieldIdFormatted, true);
      setSortedSelectedFieldsKeys((prev) => [fieldIdFormatted, ...prev]);
    },
    [addField, onSelectionChange]
  );

  const handleConfirm = () => {
    setComplianceChanged(true);
    onClose();
  };

  const handleClose = () => {
    onComplianceChange(initialValues.compliance);
    onClose();
  };

  return (
    <PositionComplianceModalWrapper>
      <Modal
        title={t('manageComplianceFields')}
        onClose={handleClose}
        prompt={JSON.stringify(complianceValue) !== JSON.stringify(initialValues.compliance)}
        size="small"
        confirmText="save"
        disabledConfirm={form.getState().invalid}
        onConfirm={handleConfirm}>
        <Container>
          <Button type="button" secondary narrow onClick={() => setSelectedField({})}>
            + {t('addField')}
          </Button>

          <FieldsArea>
            <Total>
              <Selected>{totalSelected} </Selected> {t('fieldsSelected')}
            </Total>
            {sortedSelectedFieldsKeys
              .filter((key) => !isSensitiveFieldKey(key, mappedFields))
              .map((fieldIdFormatted) => {
                const field = mappedFields[fieldIdFormatted.split('-')[1]];
                let options;

                if (field.fieldTypeId === FieldTypes.FIELD_TYPE_OPTIONS) {
                  options = field.options;
                }
                let complianceFieldValue = complianceFieldsValues[fieldIdFormatted];

                if (field.allowMultiple) {
                  // Ensure complianceFieldValue is always an array.
                  if (!Array.isArray(complianceFieldValue)) {
                    complianceFieldValue = complianceFieldValue ? [complianceFieldValue] : [];
                  }
                } else {
                  // Ensure complianceFieldValue is always a single value (or null).
                  if (Array.isArray(complianceFieldValue)) {
                    complianceFieldValue = complianceFieldValue[0];
                  }
                  complianceFieldValue = complianceFieldValue || null;
                }

                return (
                  <React.Fragment key={fieldIdFormatted}>
                    <CheckBox
                      key={fieldIdFormatted}
                      name={`compliance.fieldsIds[${fieldIdFormatted}]`}
                      tooltip={t('complianceEmploymentEndDateTooltip')}
                      value={complianceFieldsIds[fieldIdFormatted]}
                      onChange={(value) => onSelectionChange(fieldIdFormatted, value)}>
                      {field?.name !== 'end-employment-date' && t(field?.name)}
                      {field?.name === 'end-employment-date' && (
                        <ExplanationTooltip
                          title={t('end-employment-date')}
                          body={t('complianceEmploymentEndDateTooltip')}
                        />
                      )}
                    </CheckBox>
                    {complianceFieldsIds[fieldIdFormatted] && !!options && (
                      <SelectField
                        options={options.map((option) => ({ label: t(option.title), value: option.id }))}
                        name={`compliance.fieldsValues[${fieldIdFormatted}]`}
                        value={complianceFieldValue}
                        isMulti={field.allowMultiple}
                        usePortal
                        validate={required}
                      />
                    )}
                  </React.Fragment>
                );
              })}
          </FieldsArea>

          {selectedField && (
            <FieldModal
              selectedField={selectedField}
              onClose={() => setSelectedField()}
              onConfirm={handleAddField}
              allowedFieldTypes={[
                FieldTypes.FIELD_TYPE_OPTIONS,
                FieldTypes.FIELD_TYPE_DATE,
                FieldTypes.FIELD_TYPE_DOCUMENT
              ]}
            />
          )}
        </Container>
      </Modal>
    </PositionComplianceModalWrapper>
  );
};

export default PositionComplianceModal;
